import {APICore} from "./apiCore";
import {KEEP_ERROR_CODE} from "./apiCodeError";
import type { BillTo } from "./billing-details";

const api = new APICore();
const baseUrl = "/supplier-api/request";

function get(request_id: number) {
    return api.get(`${baseUrl}/${request_id}`, {});
}

function myRequestList(params: any) {
    return api.get(`${baseUrl}/me`, params);
}

/**
 * Send a draft request this supplier created to suppliers, so dispatch starts.
 * 404 for a request that isn't the supplier's own; 409 unless it is a Draft
 * with at least one service.
 */
function sendRequest(request_id: number) {
    // Keeps the code: required billing details missing from Settings are 422 BILLING_SETTINGS_INCOMPLETE.
    return api.create(`${baseUrl}/${request_id}/send`, {}, {[KEEP_ERROR_CODE]: true});
}

/**
 * Cancel a request this supplier created. Every leg and open offer is cancelled
 * and engaged suppliers are told. 404 for a request that isn't the supplier's
 * own; 409 once an offer is accepted or the request is completed.
 */
function cancelRequest(request_id: number) {
    return api.create(`${baseUrl}/${request_id}/cancel`, {});
}

/**
 * Open a request this supplier made for one of its customers (S7) to other
 * suppliers. A tied Draft is sent to the customer and pooled in one step; a sent
 * one pools every item not yet accepted. On a plain own request it is /send.
 */
function sendRequestToPool(request_id: number) {
    return api.create(`${baseUrl}/${request_id}/send-to-pool`, {}, {[KEEP_ERROR_CODE]: true});
}

function makePayment(invoice_id: number, params: any) {
    return api.create(`${baseUrl}/${invoice_id}/make-payment`, params);
}

/**
 * "Created by me": the requests this supplier company created (any of its
 * users), newest first. `status` is filtered on the server (Draft, Sent,
 * InProgress, Cancelled, Completed; empty for all).
 */
function companyRequestList(params: { status?: string; page?: number; perPage?: number }) {
    return api.get(`${baseUrl}`, params);
}

// A supplier's own billing details live in Settings (helpers/api/billing-details).
export type { BillTo, BillToType } from "./billing-details";

export interface RequestBillToState {
    /** Draft: the current Settings record (null if none). Sent: the frozen copy. */
    billTo: BillTo | null;
    /** Some item's service requires billing details (and the tier enforces it). */
    required: boolean;
    /** Sent: the copy on the request can no longer change. */
    frozen: boolean;
    /** The Settings record is saved (a prefill never counts). */
    complete: boolean;
}

/**
 * The request's billing details, read-only (plain request only). 404 for a
 * missing request; 409 BILL_TO_CUSTOMER_REQUEST on a request for a customer,
 * who adds their own.
 */
function getBillTo(request_id: number) {
    return api.get(`${baseUrl}/${request_id}/bill-to`, {});
}

export {get, getBillTo, sendRequest, sendRequestToPool, cancelRequest, myRequestList, companyRequestList, makePayment};
